import { Button, cn } from "@zaphub/ui";
import {
  CreditCard,
  Gauge,
  Info,
  ListChecks,
  LogOut,
  Megaphone,
  MessageSquare,
  Moon,
  Plug,
  Smartphone,
  Sun,
  UserPlus,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { BotaoEncerrarAcesso } from "../components/plataforma/BotaoEncerrarAcesso";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { useSessao } from "../lib/sessao";
import { useTema } from "../lib/tema";
import { useTempoReal } from "../lib/tempo-real";

const ITENS = [
  { para: "/", rotulo: "Visão geral", icone: Gauge, fim: true },
  { para: "/numeros", rotulo: "Números", icone: Smartphone },
  { para: "/campanhas", rotulo: "Campanhas", icone: Megaphone },
  { para: "/listas", rotulo: "Listas de contatos", icone: ListChecks },
  { para: "/leads", rotulo: "Leads", icone: UserPlus },
  { para: "/caixa", rotulo: "Caixa de respostas", icone: MessageSquare },
  { para: "/integracoes", rotulo: "Integrações", icone: Plug },
  { para: "/cobranca", rotulo: "Cobrança", icone: CreditCard },
  { para: "/sobre", rotulo: "Sobre", icone: Info },
] as const;

/** Casca do painel do cliente: navegação lateral, tema, usuário e stream em tempo real. */
export default function PainelLayout() {
  useTempoReal();
  const { usuario, conta, impersonadoPor, limpar } = useSessao();
  const { tema, alternar } = useTema();
  const navegar = useNavigate();
  // Conta sem assinatura ativa só enxerga a cobrança: o resto do painel está bloqueado.
  const bloqueada = Boolean(conta && !conta.assinaturaAtiva);
  const itensVisiveis = bloqueada ? ITENS.filter((i) => i.para === "/cobranca") : ITENS;

  const sair = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      limpar();
      navegar("/entrar", { replace: true });
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[15rem_1fr]">
      <aside className="border-b bg-card lg:min-h-screen lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between p-4 lg:block">
          <Logo />
          <p className="mt-0 text-xs text-muted-foreground lg:mt-1">{conta?.nome}</p>
        </div>
        <nav aria-label="Principal" className="flex gap-1 overflow-x-auto px-2 pb-2 lg:flex-col lg:overflow-visible">
          {itensVisiveis.map(({ para, rotulo, icone: Icone, ...resto }) => (
            <NavLink
              key={para}
              to={para}
              end={"fim" in resto}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                  isActive && "bg-muted font-medium text-foreground",
                )
              }
            >
              <Icone className="size-4" />
              {rotulo}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-col">
        {impersonadoPor && (
          <div role="status" className="bg-warning px-4 py-2 text-center text-sm font-medium text-warning-foreground">
            Você está acessando como <strong>{conta?.nome}</strong> (sessão de suporte, 30 min).{" "}
            <BotaoEncerrarAcesso />
          </div>
        )}
        <header className="flex items-center justify-end gap-2 border-b px-4 py-2">
          <span className="mr-auto text-sm text-muted-foreground lg:hidden">{usuario?.nome}</span>
          <span className="hidden text-sm text-muted-foreground lg:inline">{usuario?.email}</span>
          <Button variante="fantasma" tamanho="icone" onClick={alternar} aria-label="Alternar tema">
            {tema === "escuro" ? <Sun /> : <Moon />}
          </Button>
          <Button variante="fantasma" tamanho="icone" onClick={sair} aria-label="Sair">
            <LogOut />
          </Button>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
